import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  LogOut, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Filter,
  Search,
  Loader2,
  CheckCircle2,
  Eye,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, statusFilter, searchQuery]);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data.submissions);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = submissions;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredSubmissions(filtered);
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/submissions/${submissionId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubmissions(submissions.map(sub =>
        sub.id === submissionId ? { ...sub, status: newStatus } : sub
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'read': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'replied': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Subject', 'Message', 'Status'];
    const csvData = filteredSubmissions.map(sub => [
      formatDate(sub.created_at),
      sub.name,
      sub.email,
      sub.phone || 'N/A',
      sub.subject,
      sub.message.replace(/"/g, '""'), // Escape quotes
      sub.status
    ]);

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `prime-stack-submissions-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-slate-400">Manage contact form submissions</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={exportToCSV}
              variant="outline"
              disabled={filteredSubmissions.length === 0}
              className="border-green-500/30 text-green-400 hover:bg-green-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              View Website
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Total Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{submissions.length}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">New</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">
                {submissions.filter(s => s.status === 'new').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Read</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-400">
                {submissions.filter(s => s.status === 'read').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Replied</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">
                {submissions.filter(s => s.status === 'replied').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    placeholder="Search by name, email, or subject..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="read">Read</SelectItem>
                    <SelectItem value="replied">Replied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submissions Table */}
        <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-slate-800/50">
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Subject</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                        No submissions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions.map((submission) => (
                      <TableRow 
                        key={submission.id} 
                        className="border-slate-800 hover:bg-slate-800/50"
                      >
                        <TableCell className="text-slate-300 text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-500" />
                            {formatDate(submission.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {submission.name}
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          <a 
                            href={`mailto:${submission.email}`}
                            className="hover:text-orange-400 transition-colors"
                          >
                            {submission.email}
                          </a>
                        </TableCell>
                        <TableCell className="text-slate-300 max-w-xs truncate">
                          {submission.subject}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(submission.status)} border`}>
                            {submission.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedSubmission(submission)}
                              className="text-slate-400 hover:text-white hover:bg-slate-800"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Select
                              value={submission.status}
                              onValueChange={(value) => updateStatus(submission.id, value)}
                            >
                              <SelectTrigger className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="read">Read</SelectItem>
                                <SelectItem value="replied">Replied</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Detail Modal */}
        {selectedSubmission && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setSelectedSubmission(null)}
          >
            <Card 
              className="w-full max-w-2xl bg-slate-900 border-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Submission Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSubmission(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm text-slate-400 mb-1">Name</div>
                  <div className="text-white font-medium">{selectedSubmission.name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400 mb-1">Email</div>
                  <div className="text-white">
                    <a 
                      href={`mailto:${selectedSubmission.email}`}
                      className="hover:text-orange-400 transition-colors"
                    >
                      {selectedSubmission.email}
                    </a>
                  </div>
                </div>
                {selectedSubmission.phone && (
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Phone</div>
                    <div className="text-white">{selectedSubmission.phone}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-slate-400 mb-1">Subject</div>
                  <div className="text-white font-medium">{selectedSubmission.subject}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400 mb-1">Message</div>
                  <div className="text-white bg-slate-800 p-4 rounded-lg whitespace-pre-wrap">
                    {selectedSubmission.message}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <div className="text-sm text-slate-400">
                    Submitted: {formatDate(selectedSubmission.created_at)}
                  </div>
                  <Badge className={`${getStatusColor(selectedSubmission.status)} border`}>
                    {selectedSubmission.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
